import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, MapPin, Phone, Mail, Globe } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, form);
      toast.success("Message sent! We'll be in touch soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="bg-sky-50/60 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold tracking-widest text-[#1e5ba8]">CONTACT US</p>
          <h1 className="font-display font-bold text-5xl sm:text-6xl text-[#0f172a] mt-3 tracking-tight">Get in Touch</h1>
          <p className="text-slate-600 mt-4 max-w-2xl">Have a requirement? We are ready to serve you. Reach out and our team will respond promptly.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-10">
        <div>
          <h2 className="font-display font-bold text-2xl text-[#0f172a] mb-6">Contact Details</h2>
          <ul className="space-y-4 text-slate-700">
            <li className="flex items-start gap-3"><MapPin className="w-5 h-5 text-[#1e5ba8] mt-0.5" /> Office: Mumbai, India</li>
            <li className="flex items-center gap-3"><Phone className="w-5 h-5 text-[#1e5ba8]" /> +91 9821170392</li>
            <li className="flex items-start gap-3"><Mail className="w-5 h-5 text-[#1e5ba8] mt-0.5" /> <span>info@eximxpress.com<br />sales@eximxpress.com</span></li>
            <li className="flex items-center gap-3"><Globe className="w-5 h-5 text-[#1e5ba8]" /> www.eximxpress.com</li>
          </ul>

          <div className="mt-8 p-6 bg-sky-50/70 border border-sky-100 rounded-xl">
            <div className="font-display font-bold text-[#1e5ba8]">Business Hours</div>
            <div className="text-sm text-slate-600 mt-2">Mon – Sat: 9:00 AM – 7:00 PM IST</div>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-6 space-y-4 shadow-sm" data-testid="contact-form">
          <h2 className="font-display font-bold text-2xl text-[#0f172a]">Send us a Message</h2>
          <div>
            <Label htmlFor="c-name">Name</Label>
            <Input id="c-name" data-testid="contact-name-input" value={form.name} onChange={update("name")} placeholder="Your name" />
          </div>
          <div>
            <Label htmlFor="c-email">Email</Label>
            <Input id="c-email" type="email" data-testid="contact-email-input" value={form.email} onChange={update("email")} placeholder="you@company.com" />
          </div>
          <div>
            <Label htmlFor="c-sub">Subject</Label>
            <Input id="c-sub" data-testid="contact-subject-input" value={form.subject} onChange={update("subject")} placeholder="Bulk Onion Enquiry" />
          </div>
          <div>
            <Label htmlFor="c-msg">Message</Label>
            <Textarea id="c-msg" rows={5} data-testid="contact-message-input" value={form.message} onChange={update("message")} placeholder="Tell us about your requirement..." />
          </div>
          <Button type="submit" disabled={loading} className="bg-[#1e5ba8] hover:bg-[#164582] text-white font-semibold w-full py-2.5" data-testid="contact-submit-btn">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : "Send Message"}
          </Button>
        </form>
      </section>
    </div>
  );
}
